/** Display helpers for driver/carrier bids on the Box Truck board. Shared by the table indicator and
 *  the load drawer so a bid's price/label renders identically in both. */
import { displayNumeoLoadId, LoadBid } from '@numeo/types'
import type { LoadLaneFields } from './format'
import { loadLaneLabel } from './format'

/**
 * Formatters for the currencies this board actually deals in, built once.
 *
 * Constructing an `Intl.NumberFormat` is the expensive half of formatting, and the Bids column formats every
 * bid on a load for its tooltip, on every row, on every render of a virtualized board — so the common codes
 * are worth holding on to.
 *
 * A CLOSED SET rather than a cache keyed by whatever arrives. `LoadBid.currency` comes over the wire typed as
 * an unrestricted `string`, so a map keyed by it is bounded only by today's feed, and an admin tab stays open
 * all day; capping and evicting such a map would then need its own size introspection to be worth anything.
 * Two entries decided here are bounded by reading the code. Anything else builds a formatter per call —
 * exactly what every code did before this optimization, so no display changes and nothing accumulates.
 */
const CACHED_CURRENCIES: ReadonlySet<string> = new Set(['USD', 'CAD'])
const currencyFormatters = new Map<string, Intl.NumberFormat>()

const buildCurrencyFormatter = (code: string): Intl.NumberFormat =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: code, maximumFractionDigits: 0 })

const currencyFormatter = (code: string): Intl.NumberFormat => {
    // Upper-cased because `Intl` accepts either case and renders both identically, so a feed sending 'usd'
    // should hit the same held formatter rather than look like a third currency.
    const key = code.toUpperCase()
    if (!CACHED_CURRENCIES.has(key)) return buildCurrencyFormatter(key)
    const cached = currencyFormatters.get(key)
    if (cached) return cached
    const formatter = buildCurrencyFormatter(key)
    currencyFormatters.set(key, formatter)
    return formatter
}

/** The fallback's thousands separators, for a currency code `Intl` would not take. */
const PLAIN_AMOUNT_FMT = new Intl.NumberFormat('en-US')

/** "$1,250" — a whole-dollar price with the bid's currency symbol (USD default). Guards non-finite. */
export const fmtBidPrice = (price: number | null | undefined, currency?: string | null): string => {
    if (price == null || !Number.isFinite(price)) return '—'
    try {
        return currencyFormatter(currency || 'USD').format(price)
    } catch {
        // Unknown currency code → fall back to a plain amount rather than throwing.
        return `${PLAIN_AMOUNT_FMT.format(Math.round(price))} ${currency || 'USD'}`.trim()
    }
}

/** Who placed the bid, for a tooltip/list row: the bidder's name, else `fallback` (defaults to their id). */
export const bidderLabel = (bid: LoadBid, fallback?: string): string => bid.bidderName?.trim() || fallback || bid.bidderId

/** Display id used by every Box Truck bid notification. */
export const bidLoadId = (bid: LoadBid): string => displayNumeoLoadId(undefined, 'SYLECTUS_EMAIL', bid.externalId) ?? bid.externalId

/** Compact detail shared by every persistent notification surface. */
export const bidNotificationSecondaryMessage = (bid: LoadBid, load?: LoadLaneFields): string => {
    const lane = loadLaneLabel(load)
    return `Load #${bidLoadId(bid)}${lane ? ` · ${lane}` : ''}`
}

/**
 * Who on the team sent a bid email, as a person: their name, else the local part of their email, else the
 * bare id. Dispatchers know each other by name, and a raw `bekzod.numeo@gmail.com` in a narrow table cell
 * reads as a mailbox rather than as a colleague — the full address stays in the cell's tooltip.
 */
export const dispatcherLabel = (bidder: { name?: string; email?: string; id: string }): string =>
    bidder.name?.trim() || bidder.email?.split('@')[0] || bidder.id

/**
 * The bid as a dispatcher reads it on a board: WHO, then how much (ADR-057 §13) — "which driver" used to
 * be a tooltip away. Several bids name the best-priced one and count the rest.
 *
 * A first name and an initial, because "Rustam K." fits a narrow cell where "Rustam Karimov" truncates to
 * something that names nobody.
 */
export const bidsCellLabel = (bids: readonly LoadBid[]): string => {
    if (bids.length === 0) return ''
    const best = bestBid(bids)
    // Shortened only when it IS a name: "A driver" is the stand-in for a bid nobody recorded a name on,
    // and initialising it gives "A d.", which reads as a name and names nobody.
    const name = best.bidderName?.trim() ? shortBidderName(best.bidderName) : bidderLabel(best, 'A driver')
    return bids.length === 1 ? name : `${name} +${bids.length - 1}`
}

/**
 * The bid a row is named after: the HIGHEST ask.
 *
 * Highest because that is the order the server already returns them in (`ORDER BY price DESC`), so the
 * board names the row after the same bid every other surface puts first — and deterministically, which a
 * list arriving in no promised order otherwise is not: a row naming a different driver after a refresh
 * reads as a new bid.
 *
 * It is NOT "the bid the desk should take" — that is the cheapest driver, and reading the cell as a
 * recommendation is a product change to both the board and the endpoint's order, not a rename here.
 */
export const bestBid = (bids: readonly LoadBid[]): LoadBid => bids.reduce((top, bid) => (bid.price > top.price ? bid : top), bids[0])

/** `Rustam Karimov` → `Rustam K.`, and anything already short left alone. */
const shortBidderName = (name: string): string => {
    const parts = name.trim().split(/\s+/)
    if (parts.length < 2) return name.trim()
    const surname = parts[parts.length - 1]
    // Already an initial, or a one-letter surname: nothing to shorten, and `K.. ` would read as a typo.
    return `${parts[0]} ${surname.length <= 2 && surname.endsWith('.') ? surname : `${surname[0]}.`}`
}
